import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { Order } from '../models/order';
import { OrderHelper } from '../util/order-helper';
import { FirestoreService } from './firestore-utils/firestore.service';
import { ShopService } from './shop.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  private currentOrder$ = new BehaviorSubject<Order>(null);
  private currentOrderSub = new Subscription();

  constructor(
    private shopService: ShopService,
    private userService: UserService,
    private firestoreService: FirestoreService,
    private orderHelper: OrderHelper
  ) {
    this.trackCurrentOrder();
  }

  // --------------------------------------------------------------------------------------------------------
  // Current Order Functions
  // --------------------------------------------------------------------------------------------------------

  private trackCurrentOrder() {
    combineLatest([this.userService.getCurrentUser(), this.shopService.getCurrentShop()]).subscribe(async ([user, shop]) => {
      this.currentOrderSub.unsubscribe();
      if (!user || !shop) { return this.currentOrder$.next(null); }

      this.currentOrderSub = this.observeOrderBeingCreated(user.uid, shop.uid).subscribe(order => this.currentOrder$.next(order));
    });
  }

  async setCurrentOrder(order: Order) {
    order.subTotal = this.orderHelper.getOrderSubTotal(order);
    order.total    = this.orderHelper.getOrderTotal(order);

    await this.updateOrder(order);
    await this.removeOrderBeingCreatedExcept(order.uid);
  }

  getCurrentOrder(): Observable<Order> {
    return this.currentOrder$.asObservable();
  }

  // --------------------------------------------------------------------------------------------------------
  // Generic Firebase Functions
  // --------------------------------------------------------------------------------------------------------

  getOrder(uid: string): Promise<Order> {
    return this.firestoreService.getRecord('orders', uid) as Promise<Order>;
  }

  getOrders(): Promise<Order[]> {
    return this.firestoreService.getCollection('orders');
  }

  getOrdersByShop(shopId: string): Promise<Order[]> {
    return this.firestoreService.getRecordsByProperty('orders', 'shop.uid', '==', shopId) as Promise<Order[]>;
  }

  getOrdersByUser(userId: string): Promise<Order[]> {
    return this.firestoreService.getRecordsByProperty('orders', 'user.uid', '==', userId) as Promise<Order[]>;
  }

  observeOrdersByUser(userId: string): Observable<Order[]> {
    return this.firestoreService.observeRecordsByProperty('orders', 'user.uid', '==', userId);
  }

  getOrdersByUserAndShop(userId: string, shopId: string): Promise<Order[]> {
    return this.firestoreService.getRecordsByProperties('orders', ['user.uid', 'shop.uid'], '==', [userId, shopId]) as Promise<Order[]>;
  }

  getOrderBeingCreated(userId: string, shopId: string): Promise<Order> {
    return this.firestoreService.getRecordByProperties(
      'orders',
      ['user.uid', 'shop.uid', 'status'],
      '==',
      [userId, shopId, 'being-created']) as Promise<Order>;
  }

  observeOrderBeingCreated(userId: string, shopId: string): Observable<Order> {
    return this.firestoreService.observeRecordByProperties('orders',
        ['user.uid', 'shop.uid', 'status'], '==', [userId, shopId, 'being-created']);
  }

  addOrder(order: Order): Promise<Order> {
    return this.firestoreService.addOrUpdateRecord('orders', JSON.parse(JSON.stringify(order))) as Promise<Order>;
  }

  updateOrder(order: Order): Promise<Order> {
    return this.firestoreService.addOrUpdateRecord('orders', JSON.parse(JSON.stringify(order))) as Promise<Order>;
  }

  removeOrder(uid: string) {
    return this.firestoreService.removeRecord('orders', uid);
  }

  observeOrder(uid: string): Observable<Order> {
    return this.firestoreService.observeRecord('orders', uid);
  }

  observeOrders(): Observable<Order[]> {
    return this.firestoreService.observeCollection('orders');
  }

  private removeOrderBeingCreatedExcept(orderToIgnoreId: string) {
    return this.firestoreService.getRecordsByProperties(
      'orders',
      ['uid', 'status'],
      '==',
      [orderToIgnoreId, 'being-created']
    ).then(records => {
      records.forEach(record => this.removeOrder(record.uid));
    });
  }

}
